import { useEffect, useRef, useState } from "react";
import { useRegisterSW } from "virtual:pwa-register/react";
import { useTranslation } from "react-i18next";
import { Loader2 } from "lucide-react";

const UPDATE_POLL_MS = 60 * 60 * 1000;

export function UpdateOverlay() {
  const { t } = useTranslation();
  const [showOverlay, setShowOverlay] = useState(false);
  const refreshing = useRef(false);
  const isDev = import.meta.env.DEV;

  const { updateServiceWorker } = useRegisterSW({
    onRegisteredSW(_url, registration) {
      if (!registration || isDev) return;
      const tick = () => {
        void registration.update().catch(() => {});
      };
      const interval = setInterval(tick, UPDATE_POLL_MS);
      const onVisible = () => {
        if (document.visibilityState === "visible") tick();
      };
      document.addEventListener("visibilitychange", onVisible);
      return () => {
        clearInterval(interval);
        document.removeEventListener("visibilitychange", onVisible);
      };
    },
    onNeedRefresh() {
      if (isDev) return;
      setShowOverlay(true);
      void updateServiceWorker(true);
    },
  });

  useEffect(() => {
    if (!showOverlay || !("serviceWorker" in navigator)) return;
    const handler = () => {
      if (refreshing.current) return;
      refreshing.current = true;
      window.location.reload();
    };
    navigator.serviceWorker.addEventListener("controllerchange", handler);
    return () => navigator.serviceWorker.removeEventListener("controllerchange", handler);
  }, [showOverlay]);

  if (!showOverlay) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      className="fixed inset-0 z-9999 flex items-center justify-center bg-surface"
    >
      <div className="flex items-center gap-3 text-ink-700">
        <Loader2 className="size-5 animate-spin" aria-hidden="true" />
        <span>{t("pwa.update.overlay")}</span>
      </div>
    </div>
  );
}
